import React, { useState, useMemo } from "react";
import {
  Search,
  MapPin,
  Edit3, // Added Edit icon
  Trash2,
  Calendar,
  FilterX,
  Phone,
  UserCheck,
} from "lucide-react";

const VisitHistory = ({ data, onDelete, onEdit }) => {
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salesPerson, setSalesPerson] = useState("all");

  // Dummy Data
  const dummyEntries = [
    {
      id: 1,
      customerName: "Global Retailers Ltd",
      phoneNumber: "+1 415 555 0123",
      visitType: "marketing",
      salesPerson: "Alex Johnson",
      lat: 37.7749,
      lng: -122.4194,
      date: "2026-01-25",
    },
    {
      id: 2,
      customerName: "Main Street Mart",
      phoneNumber: "+1 415 555 0456",
      visitType: "collection",
      salesPerson: "Sarah Smith",
      lat: 34.0522,
      lng: -118.2437,
      date: "2026-01-27",
    },
  ];

  const allData = useMemo(() => [...data, ...dummyEntries], [data]);

  const salesPeopleList = useMemo(() => {
    return [
      "all",
      ...new Set(allData.map((item) => item.salesPerson).filter(Boolean)),
    ];
  }, [allData]);

  const filtered = useMemo(() => {
    return allData.filter((item) => {
      const matchesSearch =
        item.customerName.toLowerCase().includes(query.toLowerCase()) ||
        item.phoneNumber?.includes(query);

      const itemDate = new Date(item.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      const matchesDate =
        (!start || itemDate >= start) && (!end || itemDate <= end);

      const matchesSalesPerson =
        salesPerson === "all" || item.salesPerson === salesPerson;

      return matchesSearch && matchesDate && matchesSalesPerson;
    });
  }, [allData, query, startDate, endDate, salesPerson]);

  const clearFilters = () => {
    setQuery("");
    setStartDate("");
    setEndDate("");
    setSalesPerson("all");
  };

  return (
    <div className="space-y-4 animate-in slide-in-from-right duration-500">
      {/* FILTER CONTROLS */}
      <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
            <Search className="w-3 h-3" /> Search
          </label>
          <input
            placeholder="Name or Phone..."
            value={query}
            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> Sales Person
          </label>
          <select
            value={salesPerson}
            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            onChange={(e) => setSalesPerson(e.target.value)}
          >
            {salesPeopleList.map((person) => (
              <option key={person} value={person}>
                {person === "all" ? "All Staff" : person}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> From
          </label>
          <input
            type="date"
            value={startDate}
            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> To
          </label>
          <input
            type="date"
            value={endDate}
            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button
          onClick={clearFilters}
          className="h-[42px] flex items-center justify-center gap-2 px-4 py-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all text-sm font-bold"
        >
          <FilterX className="w-4 h-4" /> Reset
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-500 tracking-widest">
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Handled By</th>
                <th className="px-6 py-4">Map Link</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-blue-50/30 transition-colors group"
                >
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-slate-700">
                        {item.customerName}
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase font-bold tracking-tight">
                        {item.visitType}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                        {item.salesPerson?.charAt(0)}
                      </div>
                      <span className="text-xs font-medium text-slate-600">
                        {item.salesPerson}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={`https://www.google.com/maps?q=${item.lat},${item.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold hover:bg-emerald-100 transition-colors"
                    >
                      <MapPin className="w-3 h-3" /> Map View
                    </a>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {item.date}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* EDIT BUTTON */}
                      <button
                        onClick={() => onEdit(item)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit entry"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {/* DELETE BUTTON */}
                      <button
                        onClick={() => onDelete(item.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete entry"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisitHistory;
